import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

const ragCapabilities = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
      </svg>
    ),
    title: 'Multi-Modal Ingestion',
    desc: 'Video, audio, PDF, DOCX, spreadsheets, images — all indexed and semantically searchable.'
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>
    ),
    title: 'Tenant Isolation',
    desc: 'Full multi-tenant architecture. Each organization\'s data is strictly isolated at the vector store level.'
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Streaming Responses',
    desc: 'Real-time token streaming with conversation memory across multi-turn sessions.'
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    title: 'Multi-LLM Support',
    desc: 'OpenAI, Anthropic, Google Gemini, Groq, Cerebras — bring your own keys, switch providers anytime.'
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    ),
    title: 'Large File Pipeline',
    desc: 'Supports files up to 5GB per file. Background processing with status tracking.'
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    title: 'REST API',
    desc: 'Clean, documented REST API. Integrate the RAG engine into any platform or custom application.'
  },
];

const Research = () => {
  return (
    <>
      <SEO
        title="Research"
        path="/research"
        description="InjectAI Labs' flagship product: a production-ready RAG engine with multi-modal ingestion, multi-tenant isolation, streaming responses, and multi-LLM support."
      />
      <section id="research" className="py-24 pt-36 bg-dark-light">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary/10 border border-secondary/20 rounded-full text-xs text-secondary font-medium mb-5 uppercase tracking-widest">
            Flagship Product
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">
            Production-Ready RAG Engine
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            Our core research output — a Retrieval-Augmented Generation engine built for enterprise deployment. Upload any content, ask questions, get answers grounded in your data.
          </p>
        </div>

        {/* Capabilities grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {ragCapabilities.map((cap, i) => (
            <div key={i} className="bg-dark p-6 rounded-2xl border border-gray-800 hover:border-primary/40 transition-all group">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-4 group-hover:bg-primary/20 transition-colors">
                {cap.icon}
              </div>
              <h3 className="font-semibold text-white mb-2">{cap.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{cap.desc}</p>
            </div>
          ))}
        </div>

        {/* Architecture callout */}
        <div className="bg-dark border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8">
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-3">Built for Enterprise Scale</h3>
            <p className="text-gray-400 leading-relaxed">
              The RAG engine powers our WordPress and Moodle plugins, and is available directly via REST API for custom integrations. Multi-tenant, metadata-isolated, and designed to run on your infrastructure.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link to="/integrations" className="px-6 py-3 bg-primary hover:bg-blue-600 rounded-xl font-semibold text-sm transition-all text-center">
              View Integrations
            </Link>
            <Link to="/contact" className="px-6 py-3 border border-gray-700 hover:border-primary rounded-xl font-semibold text-sm transition-all text-gray-300 text-center">
              Request API Access
            </Link>
          </div>
        </div>

      </div>
    </section>
    </>
  );
};

export default Research;
